// frontend/src/components/ResidentApproval.jsx
import React, { useState, useEffect } from "react";
import api from "../config/api";
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
} from "lucide-react";
import Swal from "sweetalert2";

export const ResidentApproval = ({ user }) => {
  const [pendingResidents, setPendingResidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [count, setCount] = useState(0);

  // Verificar si hay moradores pendientes al cargar
  useEffect(() => {
    if (user?.tipo_residente === "proprietario") {
      checkPendingResidents();
    }
  }, [user]);

  const checkPendingResidents = async () => {
    try {
      const response = await api.get("/resident/has-pending");
      setHasPending(response.data.has_pending);
      setCount(response.data.count || 0);

      if (response.data.has_pending) {
        loadPendingResidents();
      }
    } catch (error) {
      console.error("Error checking pending residents:", error);
    }
  };

  const loadPendingResidents = async () => {
    setLoading(true);
    try {
      const response = await api.get("/resident/pending");
      setPendingResidents(response.data.pending_residents || []);
    } catch (error) {
      console.error("Error loading pending residents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (residentId, residentName) => {
    const result = await Swal.fire({
      title: "Aprovar morador?",
      text: `Você está prestes a aprovar ${residentName} como morador da sua unidade.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#449879",
      cancelButtonColor: "#d33",
      confirmButtonText: "✅ Sim, aprovar",
      cancelButtonText: "❌ Cancelar",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await api.post(`/resident/${residentId}/approve`);

        await Swal.fire({
          icon: "success",
          title: "✅ Morador aprovado!",
          text: `${residentName} agora pode usar a plataforma.`,
          timer: 2500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        // Recargar lista
        await loadPendingResidents();
        await checkPendingResidents();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "❌ Erro",
          text:
            error.response?.data?.message ||
            "Não foi possível aprovar o morador.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReject = async (residentId, residentName) => {
    const result = await Swal.fire({
      title: "Rejeitar morador?",
      text: `Você está prestes a rejeitar ${residentName}. Ele não poderá usar a plataforma.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#449879",
      confirmButtonText: "❌ Sim, rejeitar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await api.post(`/resident/${residentId}/reject`);

        await Swal.fire({
          icon: "info",
          title: "Morador rejeitado",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        // Recargar lista
        await loadPendingResidents();
        await checkPendingResidents();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "❌ Erro",
          text:
            error.response?.data?.message ||
            "Não foi possível rejeitar o morador.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Si no es propietario, no mostrar nada
  if (user?.tipo_residente !== "proprietario") {
    return null;
  }

  // Si no hay pendientes
  if (!hasPending && pendingResidents.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
        <Users className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">
          ✅ Não há moradores pendentes de aprovação
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-green-50 px-4 py-3 border-b border-green-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#449879]" />
            <h3 className="font-semibold text-gray-700">Moradores Pendentes</h3>
            {count > 0 && (
              <span className="bg-red-500 text-white text-xs px-2.5 py-0.5 rounded-full font-bold">
                {count}
              </span>
            )}
          </div>
          <button
            onClick={loadPendingResidents}
            disabled={loading}
            className="text-sm text-[#449879] hover:text-[#367d64] transition-colors"
          >
            🔄 Atualizar
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Estes moradores solicitaram acesso à sua unidade
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#449879] mx-auto" />
          <p className="text-sm text-gray-500 mt-2">Carregando...</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {pendingResidents.map((resident) => (
            <div
              key={resident.id}
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-800 truncate">
                    {resident.apelido || resident.name}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    <Clock className="w-3 h-3" />
                    Pendente
                  </span>
                </div>
                <p className="text-sm text-gray-500 truncate">
                  {resident.email}
                </p>
                <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                  <span>📱 {resident.phone || "Sem telefone"}</span>
                  <span>🆔 {resident.cpf}</span>
                  <span>
                    📅{" "}
                    {new Date(resident.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => handleApprove(resident.id, resident.name)}
                  disabled={loading}
                  className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                  title="Aprovar"
                >
                  <CheckCircle className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleReject(resident.id, resident.name)}
                  disabled={loading}
                  className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                  title="Rejeitar"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResidentApproval;
